import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import axiosInstance from '../../config/axiosConfig'
import { urlConfig } from '../../config/apiConfig'
import {
    Chart as ChartJS,
    LineElement,
    BarElement,
    CategoryScale,
    LinearScale,
    PointElement,
    TimeScale,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js'
import zoomPlugin from 'chartjs-plugin-zoom'
import { Line, Bar } from 'react-chartjs-2'
import 'chartjs-adapter-date-fns'
import { Box, Typography, Grid, Skeleton, Button } from '@mui/material'
import TimelineIcon from '@mui/icons-material/Timeline'
import BarChartIcon from '@mui/icons-material/BarChart'
import { Container, Loadding } from '../../components/StyleComponent'
import Statistic from '../../components/icons/Statistic'
import styled from 'styled-components'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { parseISO } from 'date-fns'
ChartJS.register(
    LineElement,
    BarElement,
    CategoryScale,
    LinearScale,
    PointElement,
    TimeScale,
    Tooltip,
    Legend,
    zoomPlugin,
    Filler,
)

const TabContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;

    font-size: 14px;
`

const Tab = styled.div`
    cursor: pointer;
    padding: 10px 20px;
    color: ${({ $active }) => ($active ? '#A81E1A' : 'black')};
    background: ${({ $active }) => ($active ? '#F7F6F6' : 'none')};
    transition: color 0.3s;
`

const Slider = styled.div`
    position: absolute;
    bottom: 0;
    height: 4px;
    width: ${({ $width }) => $width}px;
    background-color: #a81e1a;
    transition: left 0.3s, width 0.3s;
    border-radius: 10px;
    left: ${({ $left }) => $left}px;
`

const Tabs = ({
    activeTab,
    setActiveTab,
    onFetchData,
    setSelectedDate,
    setMode,
}) => {
    const [sliderProps, setSliderProps] = useState({ left: 0, width: 0 })
    const tabsRef = useRef([])
    const tabs = ['DAY', 'MONTH', 'YEAR']
    const [localSelectedDate, setLocalSelectedDate] = useState(dayjs())

    useEffect(() => {
        const activeTabElement = tabsRef.current[tabs.indexOf(activeTab)]
        if (activeTabElement) {
            setSliderProps({
                left: activeTabElement.offsetLeft,
                width: activeTabElement.clientWidth,
            })
        }
    }, [activeTab, tabs])

    const today = new Date()
    const oneYearAgo = new Date()
    oneYearAgo.setFullYear(today.getFullYear() - 1)

    return (
        <TabContainer>
            {tabs.map((tab, index) => (
                <Tab
                    key={tab}
                    $active={activeTab === tab}
                    onClick={() => setActiveTab(tab)}
                    ref={(el) => (tabsRef.current[index] = el)}
                >
                    {tab}
                </Tab>
            ))}
            {activeTab === 'DAY' && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        marginLeft: '20px',
                    }}
                >
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label='Select Date'
                            value={localSelectedDate}
                            onChange={(newValue) => {
                                const validDate = newValue || dayjs()
                                setLocalSelectedDate(validDate)
                                setSelectedDate(validDate)
                            }}
                            slotProps={{
                                textField: { size: 'small' },
                            }}
                            sx={{
                                width: '100%',
                                '& .MuiInputBase-root': {
                                    borderRadius: 30,
                                },
                            }}
                        />
                    </LocalizationProvider>
                </Box>
            )}
            {activeTab === 'MONTH' && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        marginLeft: '20px',
                    }}
                >
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label='Select Month'
                            views={['month']}
                            value={localSelectedDate}
                            minDate={dayjs(oneYearAgo)}
                            maxDate={dayjs(today)}
                            onChange={(newValue) => {
                                const validDate = newValue || dayjs()
                                setLocalSelectedDate(validDate)
                                setSelectedDate(validDate)
                            }}
                            slotProps={{
                                textField: { size: 'small' },
                            }}
                            sx={{
                                width: '100%',
                                '& .MuiInputBase-root': {
                                    borderRadius: 30,
                                },
                            }}
                        />
                    </LocalizationProvider>
                </Box>
            )}
            {activeTab === 'YEAR' && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        marginLeft: '20px',
                    }}
                >
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label='Select Year'
                            views={['year']}
                            value={localSelectedDate}
                            onChange={(newValue) => {
                                const validDate = newValue || dayjs()
                                setLocalSelectedDate(validDate)
                                setSelectedDate(validDate)
                            }}
                            slotProps={{
                                textField: { size: 'small' },
                            }}
                            sx={{
                                width: '100%',
                                '& .MuiInputBase-root': {
                                    borderRadius: 30,
                                },
                            }}
                        />
                    </LocalizationProvider>
                </Box>
            )}
            <Button
                type='submit'
                variant='contained'
                color='buttonSave'
                sx={{
                    borderRadius: '30px',
                    minWidth: '100px',
                    marginLeft: '5px',
                }}
                onClick={() => {
                    setMode('HISTORICAL')
                    onFetchData(localSelectedDate, activeTab)
                }}
            >
                Search
            </Button>

            <Slider $left={sliderProps.left} $width={sliderProps.width} />
        </TabContainer>
    )
}

function GraphNew({ admin, row }) {
    const chartRefs = useRef({})
    const chartInstanceRef = useRef()
    const [log, setLog] = useState([])
    const [historicalLog, setHistoricalLog] = useState([])
    const [device, setDevice] = useState('')
    const { deviceId } = useParams()
    const [minY, setMinY] = useState(0)
    const [maxY, setMaxY] = useState(0)
    const [mode, setMode] = useState('REALTIME')
    const [activeTab, setActiveTab] = useState('DAY')
    const [chart, setChart] = useState('Line')
    const [selectedParameters, setSelectedParameters] = useState([])
    const [detailParam, setDetailParam] = useState([])
    const [graphParams, setGraphParams] = useState([])
    const [selectedDate, setSelectedDate] = useState(dayjs())

    const [isLoading, setIsLoading] = useState(false)
    const [tabLoading, setTabLoading] = useState(false)
    const [chartLoading, setChartLoading] = useState(false)
    const selectedDayjs = dayjs(selectedDate)
    const streamingIntervalRef = useRef(null)

    const [loading, setLoading] = useState(false)

    const selectedDeviceId = admin ? row?.device_id : deviceId

    const handleTabChange = (newTab) => {
        setTabLoading(true)
        setActiveTab(newTab)
    }

    const handleChartChange = (newChart) => {
        setChartLoading(true)
        setChart(newChart)

        setTimeout(() => {
            setChartLoading(false)
        }, 500)
    }

    const getDaysInMonth = (year, month) => {
        return new Date(year, month + 1, 0).getDate()
    }

    const now = new Date()
    const currentYear = now.getFullYear()
    const currentMonth = now.getMonth()
    const daysInCurrentMonth = getDaysInMonth(currentYear, currentMonth)

    const mergedData =
        activeTab === 'MONTH' && historicalLog
            ? Array.from({ length: daysInCurrentMonth }, (_, i) => {
                  const day = i + 1
                  const existingData = historicalLog.find((d) => {
                      const date = new Date(d.date)
                      return date.getDate() === day
                  })
                  const baseDate = new Date(
                      selectedDayjs.year(),
                      selectedDayjs.month(),
                      day,
                  )
                  return existingData
                      ? {
                            ...existingData,
                            name: day.toString(),
                            x: existingData.date || baseDate,
                        }
                      : {
                            name: day.toString(),
                            x: baseDate,
                        }
              }).sort((a, b) => new Date(a.x) - new Date(b.x))
            : []

    const mergedYear =
        activeTab === 'YEAR' && historicalLog
            ? Array.from({ length: 12 }, (_, i) => {
                  const existingData = historicalLog.find((d) => {
                      const date = new Date(d.date)
                      return date.getMonth() === i
                  })
                  const baseDate = new Date(selectedDayjs.year(), i, 1)
                  return existingData
                      ? {
                            ...existingData,
                            name: (i + 1).toString(),
                            x: existingData.date || baseDate,
                        }
                      : {
                            name: (i + 1).toString(),
                            x: baseDate,
                        }
              }).sort((a, b) => new Date(a.x) - new Date(b.x))
            : []

    const downsampleData = (data, maxPoints = 500) => {
        if (!Array.isArray(data) || data.length <= maxPoints) return data

        const step = Math.max(1, Math.floor(data.length / maxPoints))
        const downsampled = []

        for (let i = 0; i < data.length; i += step) {
            downsampled.push(data[i])
        }

        if (data.length % step !== 0) {
            downsampled.push(data[data.length - 1])
        }

        return downsampled
    }

    const getDatasetColor = (index) => {
        const colors = [
            '#4A90E2', // Blue
            '#50E3C2', // Green
            '#F5A623', // Orange
            '#D0021B', // Red
            '#9013FE', // Purple
            '#B8E986', // Light Green
            '#F8E71C', // Yellow
            '#8B572A', // Brown
            '#417505', // Dark Green
            '#BD10E0', // Pink
        ]
        return colors[index % colors.length]
    }
    const formattedData = useMemo(() => {
        const source = mode === 'HISTORICAL' ? historicalLog : log
        if (!source?.length || !selectedParameters?.length) return []
        return source.map((entry) => {
            const point = { x: new Date(entry.date) }
            selectedParameters.forEach((key) => {
                point[key] = entry[key] ?? 0
            })
            return point
        })
    }, [historicalLog, log, selectedParameters, mode])

    const chartData = useMemo(() => {
        let optimizedData = formattedData

        if (chart === 'Bar') {
            optimizedData = downsampleData(formattedData, 500)
        }

        if (!formattedData.length || !selectedParameters?.length) {
            return { labels: [], datasets: [] }
        }

        return {
            labels: optimizedData.map((d) => d.x),
            datasets: selectedParameters.map((key, index) => {
                return {
                    label: key || 'No Data',
                    data: optimizedData.map((d) => ({
                        x: d.x,
                        y: d[key],
                    })),
                    borderColor: getDatasetColor(index),
                    fill: chart === 'Bar',
                    tension: 0.4,
                    pointRadius: 0,
                    borderWidth: 2,
                    pointRadius: (context) => {
                        const index = context.dataIndex
                        return index === optimizedData.length - 1 ? 2 : 0
                    },
                    pointStyle: 'circle',
                }
            }),
        }
    }, [formattedData, selectedParameters, chart])

    const generateChartData = (data = [], key, index) => {
        if (!Array.isArray(data)) {
            console.error(
                `Invalid data passed to generateChartData for key: ${key}`,
                data,
            )
            return {
                labels: [],
                datasets: [],
            }
        }

        const colors = [
            '#4A90E2', // Blue
            '#50E3C2', // Green
            '#F5A623', // Orange
            '#D0021B', // Red
            '#9013FE', // Purple
            '#B8E986', // Light Green
            '#F8E71C', // Yellow
            '#8B572A', // Brown
            '#417505', // Dark Green
            '#BD10E0', // Pink
        ]

        const color = colors[index % colors.length] || '#000000'

        const ctx = chartInstanceRef.current?.ctx
        const gradient = ctx?.createLinearGradient(0, 0, 0, 400)

        if (gradient) {
            gradient.addColorStop(0, `${color}80`)
            gradient.addColorStop(1, `${color}20`)
        }

        return {
            labels: data.map((d) => d.x || ''),
            datasets: [
                {
                    label: key || 'No Data',
                    data: data.map((d) => ({
                        x: d.x || new Date(),
                        y: d[key] || 0,
                    })),
                    borderColor: color,
                    backgroundColor: gradient || color,
                    fill: chart === 'Bar',
                    tension: 0.4,
                    borderWidth: 2,
                    pointRadius: (context) => {
                        const idx = context.dataIndex
                        return idx === data.length - 1 ? 2 : 0
                    },
                    pointStyle: 'circle',
                },
            ],
        }
    }

    // useEffect(() => {
    //     console.log('chartData sample', chartData?.[0])
    //     console.log('mergedData sample', mergedData?.[0])
    //     console.log('mergedYear sample', mergedYear?.[0])
    //     console.log('detailParam', detailParam)
    // }, [chartData, mergedData, mergedYear, detailParam])

    useEffect(() => {
        const getDataDevice = async () => {
            if (!selectedDeviceId) return
            setIsLoading(true)
            try {
                const response = await axiosInstance.get(
                    `${urlConfig.deviceById}/${selectedDeviceId}`,
                )
                const deviceData = response.data.message
                setDevice(deviceData)

                if (deviceData.min_y !== undefined) setMinY(deviceData.min_y)
                if (deviceData.max_y !== undefined) setMaxY(deviceData.max_y)

                setDetailParam(deviceData.params || [])

                const graphParamsData =
                    deviceData.params?.filter((p) => p.show_graph) || []
                setGraphParams(graphParamsData)

                if (graphParamsData.length === 0) {
                    setSelectedParameters([])
                } else {
                    setSelectedParameters(graphParamsData.map((p) => p.key))
                }
            } catch (error) {
                console.error('Error fetching device data:', error)
            } finally {
                setIsLoading(false)
            }
        }

        getDataDevice()
    }, [selectedDeviceId, deviceId])

    const fetchData = useCallback(async () => {
        if (!selectedDeviceId || !selectedParameters?.length) return null
        const validDate = selectedDate.toDate()
        let formattedDateOnly
        if (activeTab === 'MONTH') {
            formattedDateOnly = dayjs(validDate).format('YYYY-MM')
        } else if (activeTab === 'YEAR') {
            formattedDateOnly = dayjs(validDate).format('YYYY')
        } else if (activeTab === 'DAY') {
            formattedDateOnly = validDate.toLocaleDateString('en-CA')
        }
        setLoading(true)

        try {
            const response = await axiosInstance.get(
                `${
                    urlConfig.realtime
                }/${selectedDeviceId}?period=${activeTab.toLowerCase()}&name_parameter=${selectedParameters}&date=${formattedDateOnly}`,
            )
            let logData = response.data.message || []
            logData = logData.map((entry) => {
                let formattedDate
                if (activeTab === 'DAY') {
                    const [hours, minutes, seconds] = entry.name
                        .split(':')
                        .map(Number)
                    formattedDate = new Date(
                        now.getFullYear(),
                        now.getMonth(),
                        now.getDate(),
                        hours,
                        minutes,
                        seconds,
                    )
                } else if (activeTab === 'MONTH' || activeTab === 'YEAR') {
                    formattedDate = parseISO(entry.name)
                }
                const result = { date: formattedDate }
                selectedParameters.forEach((key) => {
                    const value = Number(entry[key])
                    result[key] = isNaN(value) ? 0 : value
                })
                return result
            })
            logData.sort((a, b) => new Date(a.date) - new Date(b.date))

            const MAX_RECENT = 500
            const MAX_TOTAL = 2000
            const recentData = logData.slice(-MAX_RECENT) // 200 จุดล่าสุด
            const olderData = logData.slice(0, logData.length - MAX_RECENT) // ก่อนหน้านั้น

            const downsampleData = (data, target) => {
                if (data.length <= target) return data
                const ratio = Math.floor(data.length / target)
                return data.filter((_, index) => index % ratio === 0)
            }

            const downsampledOld = downsampleData(
                olderData,
                MAX_TOTAL - MAX_RECENT,
            )
            const finalData = [...downsampledOld, ...recentData]

            setLog(finalData)
            setTabLoading(false)
            return logData
        } catch (error) {
            console.error('Error fetching data:', error)
            setTabLoading(false)
            return null
        } finally {
            setLoading(false)
        }
    }, [activeTab, selectedDeviceId, selectedParameters, selectedDate])

    useEffect(() => {
        if (selectedDeviceId && selectedParameters.length > 0) {
            setMode('REALTIME')
            fetchData()
        }
    }, [activeTab, selectedDeviceId, selectedParameters])

    const handleFetchData = async (firstDate, activeTab) => {
        setTabLoading(true)
        try {
            const djs = dayjs(firstDate) // Always treat `firstDate` as Day.js object
            let formattedDateOnly = ''

            if (activeTab === 'MONTH') {
                formattedDateOnly = djs.format('YYYY-MM')
            } else if (activeTab === 'YEAR') {
                formattedDateOnly = djs.format('YYYY')
            } else if (activeTab === 'DAY') {
                formattedDateOnly = djs.format('YYYY-MM-DD')
            }

            const response = await axiosInstance.get(
                `${
                    urlConfig.realtime
                }/${selectedDeviceId}?period=${activeTab.toLowerCase()}&name_parameter=${selectedParameters}&date=${formattedDateOnly}`,
            )

            let historyData = response.data.message

            historyData = historyData.map((entry) => {
                let formattedDate

                if (activeTab === 'DAY') {
                    const [hours, minutes, seconds] = entry.name
                        .split(':')
                        .map(Number)

                    formattedDate = djs
                        .hour(hours)
                        .minute(minutes)
                        .second(seconds)
                        .toDate()
                } else if (activeTab === 'MONTH' || activeTab === 'YEAR') {
                    formattedDate = parseISO(entry.name) // or use dayjs(entry.name).toDate()
                }

                const result = { date: formattedDate }

                // Ensure selectedParameters is iterable (array)
                ;(Array.isArray(selectedParameters)
                    ? selectedParameters
                    : [selectedParameters]
                ).forEach((key) => {
                    const value = Number(entry[key])
                    result[key] = isNaN(value) ? 0 : value
                })

                return result
            })
            historyData.sort((a, b) => new Date(a.date) - new Date(b.date))
            setHistoricalLog(historyData)

            return historyData
        } catch (error) {
            console.error('Error fetching historical data:', error)
        } finally {
            setTabLoading(false)
        }
    }

    useEffect(() => {
        if (activeTab === 'DAY' && mode === 'HISTORICAL') {
            handleFetchData(selectedDate, activeTab)
        } else if (activeTab === 'DAY' && mode !== 'HISTORICAL') {
            setMode('REALTIME')
            fetchData()
        }
    }, [activeTab, selectedDate, selectedDeviceId, selectedParameters, mode])

    useEffect(() => {
        if (activeTab === 'MONTH' || activeTab === 'YEAR') {
            setMode('HISTORICAL')
            handleFetchData(selectedDate, activeTab)
        }
    }, [activeTab, selectedDate, selectedParameters])

    useEffect(() => {
        if (!historicalLog.length || !selectedParameters.length) return

        const datasets = selectedParameters.map((key, index) => {
            const data = historicalLog.map((entry) => ({
                x: entry.date,
                y: entry[key],
            }))

            return {
                label: key,
                data: downsampleData(data, 500),
                borderColor: getDatasetColor(index),
                fill: chart === 'Bar',
                tension: 0.4,
                pointRadius: 0,
                borderWidth: 2,
                pointRadius: (context) => {
                    const index = context.dataIndex
                    return index === data.length - 1 ? 2 : 0
                },
                pointStyle: 'circle',
            }
        })

        if (chartInstanceRef.current) {
            chartInstanceRef.current.data.datasets = datasets
            chartInstanceRef.current.update('none')
        }
    }, [historicalLog, selectedParameters])

    useEffect(() => {
        if (activeTab !== 'DAY') return
        if (!log.length || !selectedParameters?.length) return

        const datasets = selectedParameters.map((key, index) => {
            const paramDetail = detailParam.find((p) => p.key === key)
            const label = paramDetail?.name || key

            const data = log
                .filter(
                    (entry) =>
                        entry[key] !== undefined && entry.date !== undefined,
                )
                .map((entry) => ({
                    x: entry.date,
                    y: entry[key],
                }))

            return {
                label: label,
                data: data,
                borderColor: getDatasetColor(index),
                fill: chart === 'Bar',
                tension: 0.4,
                pointRadius: 0,
                borderWidth: 2,
                pointRadius: (context) => {
                    const index = context.dataIndex
                    return index === data.length - 1 ? 2 : 0
                },
                pointStyle: 'circle',
            }
        })

        if (chartInstanceRef.current) {
            chartInstanceRef.current.data.datasets = datasets
            chartInstanceRef.current.update('none')
        }
    }, [log, selectedParameters, activeTab])

    // useEffect(() => {
    //     if (mode === 'HISTORICAL' || activeTab !== 'DAY') {
    //         if (streamingIntervalRef.current) {
    //             clearInterval(streamingIntervalRef.current)
    //             streamingIntervalRef.current = null
    //         }
    //         return
    //     }

    //     streamingIntervalRef.current = setInterval(async () => {
    //         try {
    //             const newData = await fetchData()
    //             if (!newData?.length) return

    //             const latestEntry = newData[newData.length - 1]

    //             selectedParameters.forEach((key, index) => {
    //                 const dataset =
    //                     chartInstanceRef.current?.data?.datasets[index]
    //                 if (!dataset) return

    //                 const previous = dataset.data[dataset.data.length - 1]
    //                 if (previous && previous.y === latestEntry[key]) return // skip if same

    //                 dataset.data.push({
    //                     x: latestEntry.date,
    //                     y: latestEntry[key],
    //                 })

    //                 // Keep the chart light & stable
    //                 if (dataset.data.length > 500) {
    //                     dataset.data = downsampleData(dataset.data, 500)
    //                 }
    //             })

    //             requestAnimationFrame(() => {
    //                 chartInstanceRef.current?.update('none')
    //             })
    //         } catch (error) {
    //             console.error('Error updating chart:', error)
    //         }
    //     }, 1000)

    //     return () => {
    //         if (streamingIntervalRef.current) {
    //             clearInterval(streamingIntervalRef.current)
    //             streamingIntervalRef.current = null
    //         }
    //     }
    // }, [mode, activeTab, selectedParameters, fetchData])
    useEffect(() => {
        let isActive = true

        if (mode === 'HISTORICAL' || activeTab !== 'DAY') return

        const loopFetch = async () => {
            while (isActive) {
                try {
                    const newData = await fetchData()
                    if (newData?.length) {
                        const latestEntry = newData[newData.length - 1]

                        selectedParameters.forEach((key, index) => {
                            const dataset =
                                chartInstanceRef.current?.data?.datasets[index]
                            if (!dataset) return

                            const previous =
                                dataset.data[dataset.data.length - 1]
                            if (previous && previous.y === latestEntry[key])
                                return

                            dataset.data.push({
                                x: latestEntry.date,
                                y: latestEntry[key],
                            })

                            if (dataset.data.length > 500) {
                                dataset.data = downsampleData(dataset.data, 500)
                            }
                        })

                        requestAnimationFrame(() => {
                            chartInstanceRef.current?.update('none')
                        })
                    }
                } catch (error) {
                    console.error('Error updating chart:', error)
                }

                // รอ 1 วินาทีก่อน loop ถัดไป
                await new Promise((resolve) => setTimeout(resolve, 1000))
            }
        }

        loopFetch() // เริ่ม loop

        return () => {
            isActive = false // หยุด loop เมื่อ component ถูก unmount หรือ dependency เปลี่ยน
        }
    }, [mode, activeTab, selectedParameters, fetchData])

    useEffect(() => {
        if (activeTab === 'DAY') {
            setSelectedDate(dayjs())
        }
    }, [activeTab])

    useEffect(() => {
        if (activeTab === 'DAY') {
            const today = new Date()
            const selected = new Date(selectedDate)

            const isSameDay =
                today.getFullYear() === selected.getFullYear() &&
                today.getMonth() === selected.getMonth() &&
                today.getDate() === selected.getDate()

            if (mode === 'HISTORICAL' && isSameDay) {
                setMode('REALTIME')
            }
        }
    }, [selectedDate, activeTab, mode])

    // Memoize options to prevent chartjs-plugin-zoom from resetting zoom/pan state
    function options(data) {
        const minY = data.min_y || 0
        const maxY = data.max_y || 0
        const shouldAutoScaleY = minY === 0 && maxY === 0

        const timeUnit =
            activeTab === 'DAY'
                ? 'minute'
                : activeTab === 'MONTH'
                ? 'day'
                : 'month'

        const range = maxY - minY || 1
        const gap =
            activeTab === 'YEAR' || activeTab === 'MONTH'
                ? Math.max(range * 0.1, 2)
                : 0

        const adjustedMinY = activeTab === 'DAY' ? minY : Math.floor(minY)
        const adjustedMaxY = activeTab === 'DAY' ? maxY : Math.ceil(maxY + gap)

        let xMin, xMax
        if (activeTab === 'DAY') {
            // 00:00:00 to 23:59:59 of selectedDate
            const d = dayjs(selectedDate)
            xMin = d.startOf('day').toDate()
            xMax = d.endOf('day').toDate()
        } else if (activeTab === 'MONTH') {
            // first day to last day of selected month
            const d = dayjs(selectedDate)
            xMin = d.startOf('month').toDate()
            xMax = d.endOf('month').toDate()
        } else if (activeTab === 'YEAR') {
            // Jan 1 to Dec 31 of selected year
            const d = dayjs(selectedDate)
            xMin = d.startOf('year').toDate()
            xMax = d.endOf('year').toDate()
        }

        return {
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            interaction: {
                mode: 'nearest',
                intersect: false,
            },
            scales: {
                x: {
                    type: 'time',
                    time: {
                        unit: timeUnit,
                        displayFormats: {
                            minute: 'HH:mm',
                            day: 'MMM d',
                            month: 'MMM yyyy',
                        },
                    },
                    min: xMin,
                    max: xMax,
                    grid: {
                        display: true,
                        color: 'rgba(0, 0, 0, 0.05)',
                    },
                    ticks: {
                        color: '#333',
                        font: {
                            size: 13,
                            weight: '500',
                        },
                        padding: 6,
                    },
                    title: {
                        display: true,
                        color: '#333',
                        font: {
                            size: 13,
                            weight: '500',
                        },
                    },
                },
                y: {
                    ...(shouldAutoScaleY
                        ? {}
                        : {
                              min: adjustedMinY,
                              max: adjustedMaxY,
                          }),
                    // min: adjustedMinY,
                    // max: adjustedMaxY,
                    grid: {
                        color: 'rgba(0, 0, 0, 0.05)',
                        lineWidth: 1,
                    },
                    ticks: {
                        color: '#333',
                        font: {
                            size: 13,
                            weight: '500',
                        },
                        padding: 6,
                    },
                    title: {
                        display: true,
                        text:
                            graphParams.length === 1
                                ? `${graphParams[0].name} (${graphParams[0].unit})`
                                : 'Values',
                        color: '#222',
                        font: {
                            size: 14,
                            weight: '400',
                        },
                        padding: {
                            top: 10,
                            bottom: 4,
                        },
                    },
                },
            },
            plugins: {
                decimation: {
                    enabled: true,
                    algorithm: 'lttb',
                    samples: 500,
                },
                tooltip: {
                    enabled: true,
                    mode: 'nearest',
                    intersect: false,
                    callbacks: {
                        title: (tooltipItems) => {
                            const date = tooltipItems[0]?.raw?.x
                            if (activeTab === 'DAY') {
                                return new Date(date).toLocaleTimeString(
                                    'en-GB',
                                    {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                    },
                                )
                            } else if (activeTab === 'MONTH') {
                                return new Date(date).toLocaleDateString(
                                    'en-GB',
                                    {
                                        day: '2-digit',
                                        month: 'short',
                                    },
                                )
                            } else if (activeTab === 'YEAR') {
                                return new Date(date).toLocaleDateString(
                                    'en-GB',
                                    {
                                        month: 'short',
                                        year: 'numeric',
                                    },
                                )
                            }
                            return date
                        },
                        label: (context) => {
                            const key = context.dataset.label
                            // Find the unit from detailParam
                            const param = detailParam.find((p) => p.key === key)
                            const unit = param?.unit ? ` ${param.unit}` : ''
                            const value = context.raw.y ?? 0
                            return `${key}: ${value}${unit}`
                        },
                    },
                },
                legend: {
                    display: true,
                    position: 'top',
                },
                zoom:
                    activeTab === 'DAY'
                        ? {
                              zoom: {
                                  wheel: { enabled: true },
                                  pinch: { enabled: true },
                                  mode: 'x',
                              },
                              pan: {
                                  enabled: true,
                                  mode: 'x',
                              },
                              limits: {
                                  x: { min: xMin, max: xMax },
                              },
                              onPanComplete: () => {},
                              onZoomComplete: () => {},
                              onPanRejected: () => {},
                              onZoomRejected: () => {},
                          }
                        : {
                              zoom: {
                                  wheel: { enabled: false },
                                  pinch: { enabled: false },
                                  mode: 'x',
                              },
                              pan: {
                                  enabled: false,
                                  mode: 'x',
                              },
                              limits: {
                                  x: { min: xMin, max: xMax },
                              },
                              onPanComplete: () => {},
                              onZoomComplete: () => {},
                              onPanRejected: () => {},
                              onZoomRejected: () => {},
                          },
            },
        }
        // Only recalculate options when these change:
        // Avoid including objects/arrays/functions that change every render!
    }
    const filteredItems = useMemo(() => {
        return detailParam?.filter((item) => item.show_graph) || []
    }, [detailParam])

    const chartOptionsMap = useMemo(() => {
        const map = {}
        for (const item of filteredItems) {
            map[item.key] = options(item)
        }
        return map
    }, [filteredItems, activeTab, selectedDate, graphParams, detailParam])
    if (isLoading) {
        return (
            <Container
                header={header}
                navigate={{ title: selectedDeviceId, navigate: '/graph-trend' }}
            >
                <Grid container>
                    <Grid item xs={12}>
                        <Box
                            sx={{
                                minHeight: 'calc(100vh - 100px)',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <Loadding />
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        )
    }

    const content = (
        <Grid container spacing={2} p={2}>
            <Grid
                item
                xs={12}
                md={8}
                sx={{ display: 'flex', alignItems: 'center' }}
            >
                <img
                    src={`${urlConfig.baseUrlAPI}/api/v1${device.image}`}
                    alt='device'
                    width={60}
                    height={60}
                />
                <Box ml={2}>
                    <Typography variant='subtitle1' fontWeight='bold'>
                        {device?.device_type || <Skeleton width={100} />}
                    </Typography>
                    <Typography variant='body2'>
                        ID: {device?.device_id || <Skeleton width={60} />}
                    </Typography>
                </Box>
            </Grid>

            <Grid item xs={12}>
                <Box
                    sx={{
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        p: 2,
                        backgroundColor: '#fff',
                    }}
                >
                    <Grid container spacing={2} alignItems='center'>
                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    flexWrap: 'wrap',
                                    rowGap: 2,
                                }}
                            >
                                <Tabs
                                    activeTab={activeTab}
                                    setActiveTab={handleTabChange}
                                    onFetchData={handleFetchData}
                                    setSelectedDate={setSelectedDate}
                                    setMode={setMode}
                                />
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: '30px',
                                        padding: '4px 6px',
                                        border: '1px solid lightgray',
                                    }}
                                >
                                    <button
                                        style={{
                                            cursor: 'pointer',
                                            padding: '6px 12px',
                                            border: 'none',
                                            borderRadius: '30px',
                                            backgroundColor:
                                                chart === 'Line'
                                                    ? '#A81E1A'
                                                    : '#f5f5f5',
                                            color:
                                                chart === 'Line'
                                                    ? '#f5f5f5'
                                                    : '#A81E1A',
                                            boxShadow:
                                                chart === 'Line'
                                                    ? '0px 4px 8px rgba(168, 30, 26, 0.3)'
                                                    : 'none',
                                            transition: 'all 0.3s ease',
                                        }}
                                        onClick={() =>
                                            handleChartChange('Line')
                                        }
                                    >
                                        <TimelineIcon
                                            sx={{
                                                color:
                                                    chart === 'Line'
                                                        ? '#f5f5f5'
                                                        : '#A81E1A',
                                                fontSize: 24,
                                            }}
                                        />
                                    </button>
                                    <button
                                        style={{
                                            cursor: 'pointer',
                                            padding: '6px 12px',
                                            border: 'none',
                                            borderRadius: '30px',
                                            backgroundColor:
                                                chart === 'Bar'
                                                    ? '#A81E1A'
                                                    : '#f5f5f5',
                                            color:
                                                chart === 'Bar'
                                                    ? '#f5f5f5'
                                                    : '#A81E1A',
                                            boxShadow:
                                                chart === 'Bar'
                                                    ? '0px 4px 8px rgba(168, 30, 26, 0.3)'
                                                    : 'none',
                                            transition: 'all 0.3s ease',
                                        }}
                                        onClick={() => handleChartChange('Bar')}
                                    >
                                        <BarChartIcon
                                            sx={{
                                                color:
                                                    chart === 'Bar'
                                                        ? '#f5f5f5'
                                                        : '#A81E1A',
                                                fontSize: 24,
                                            }}
                                        />
                                    </button>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        {tabLoading || isLoading ? (
                            <Box
                                sx={{
                                    minHeight: '400px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <Loadding />
                            </Box>
                        ) : log ? (
                            <Box sx={{ minHeight: '400px' }}>
                                {log[0] &&
                                detailParam &&
                                detailParam.some((item) => item.show_graph) ? (
                                    detailParam
                                        .filter((item) => item.show_graph)
                                        .map((item, index) => {
                                            if (!chartRefs.current[item.key]) {
                                                chartRefs.current[item.key] =
                                                    React.createRef()
                                            }

                                            const ref =
                                                chartRefs.current[item.key]

                                            const data =
                                                activeTab === 'YEAR'
                                                    ? generateChartData(
                                                          mergedYear,
                                                          item.key,
                                                          index,
                                                      )
                                                    : activeTab === 'DAY'
                                                    ? generateChartData(
                                                          formattedData,
                                                          item.key,
                                                          index,
                                                      )
                                                    : generateChartData(
                                                          mergedData,
                                                          item.key,
                                                          index,
                                                      )

                                            const ChartComponent =
                                                chart === 'Line' ? Line : Bar
                                            const chartOptions =
                                                chartOptionsMap[item.key]
                                            return (
                                                <Box
                                                    key={index}
                                                    sx={{
                                                        width: '100%',
                                                        height: '300px',
                                                        position: 'relative',
                                                        marginBottom: '20px',
                                                        mt: 2,
                                                    }}
                                                >
                                                    <ChartComponent
                                                        ref={ref}
                                                        data={data}
                                                        options={chartOptions}
                                                        width={'100%'}
                                                        height={'300px'}
                                                    />
                                                </Box>
                                            )
                                        })
                                ) : loading ? (
                                    <Box
                                        sx={{
                                            marginTop: 25,
                                            minHeight: '400px',
                                            display: 'flex',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Loadding />
                                    </Box>
                                ) : (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            minHeight: '400px',
                                            padding: '100px 0',
                                        }}
                                    >
                                        Not Found
                                    </Box>
                                )}
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    minHeight: '400px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                }}
                            >
                                <Loadding />
                            </Box>
                        )}
                    </Grid>
                </Box>
            </Grid>
        </Grid>
    )

    return admin ? (
        content
    ) : (
        <Container
            header={header}
            navigate={{ title: selectedDeviceId, navigate: '/graph-trend' }}
        >
            {content}
        </Container>
    )
}

export default GraphNew

const header = {
    icon: <Statistic sx={{ color: 'white' }} />,
    color: 'grey',
    title: 'Graph Trend',
}
